import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
    Button 
} from 'reactstrap';

import { 
    CompareCreationTime,
    CompareStringsDefaultToCreationTime,
    GetSubmissionStatusIconColumn, 
    GetSubmissionStatus,
    GetWorkspaceDisplayName,
} from '../ComponentUtils/SubmissionUtils';
import { BrandConfig, GetBrandConfig } from '../../Store/Tenant';
import { LinkMode } from '../../Store/ImportFinancials';
import { GetLoggedInUser, GetUsers } from '../../Store/User';
import { FormatDate, formatStringMDYFullMonth } from '../../Utils/DateUtils';
import { DeleteSubmission } from '../../Services/SubmissionService';
import {
    actionCreators as WorkspacesActions,
    LoadWorkspaceDetails,
} from '../../Store/Workspaces';
import { actionCreators as SubmissionHistoryActions } from '../../Store/SubmissionHistory';
import { ApplicationState } from '../../Store';
import { GetPortalText } from '../../Store/PortalAssets';
import {
    actionCreators as UIStateActions,
    GetPortalBusyState,
} from '../../Store/UIState';
import { ErrorState, ErrorBanner } from '../ErrorBanner/ErrorBanner';

import { FinancialsRelinkButton } from '../FinancialsImport/FinancialsRelinkButton';
import { ConfirmModal, ConfirmModalType } from '../ConfirmModal/ConfirmModal';

import { 
    SubmissionSearchResult,
    UserResponse,
    PortalTextCollectionsRequiringAttention,
    PortalTextCommon,
    PortalTextGeneralMessages,
} from '../../Models/Api/strongbox.financialportal';

import {
    Col,
    Row,
} from 'reactstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faInfoCircle,
    faTrash,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';

import { 
    SortableTable, 
    SortableTableColumn, 
    SortableTableSortInformation,
} from '../SortableTable/SortableTable';

import {
    Tooltip
} from '@mui/material';

import UsersIcon from '@mui/icons-material/GroupAdd';

type InjectedReduxState = {
    brandConfig: BrandConfig;
    portalBusy: boolean;
    allUsers: UserResponse[];   // needed when the user is an admin and wants to manage access to a workspace
    loggedInUser?: UserResponse;
    craText?: PortalTextCollectionsRequiringAttention;
    commonText?: PortalTextCommon;
    generalMessages?: PortalTextGeneralMessages;
};

type InjectedActionCreators = 
    typeof WorkspacesActions & 
    typeof UIStateActions & 
    typeof SubmissionHistoryActions;

type SubmissionsPendingLenderInterventionProps = {
    submissions: SubmissionSearchResult[];
    onSubmissionDeleted?: (submission: SubmissionSearchResult) => void;
};

type Props = SubmissionsPendingLenderInterventionProps & InjectedReduxState & InjectedActionCreators;

export const SubmissionsPendingLenderInterventionComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        allUsers,
        brandConfig,
        submissions,
        portalBusy,
        SetPortalWorking,
        SetPortalIdle,
        SetModifyWorkspaceUsersState,
        SubmissionDeleted,
        onSubmissionDeleted,
        craText,
        commonText,
        generalMessages,
    } = props;

    const [sortState, setSortState] = React.useState<SortableTableSortInformation>({
        sortColumn: 1,           // Creation time column
        sortType: 'ascending',
    });
    const [confirmDelete, setConfirmDelete] = React.useState<SubmissionSearchResult | undefined>(undefined);

    const [errorState, setErrorState] = React.useState<ErrorState | undefined>(undefined);

    const getLinkContent = (submission: SubmissionSearchResult): React.ReactElement => {
        return (
            <FinancialsRelinkButton
                datasourceNameId={submission.submission.datasourceNameId}
                disabledByProp={portalBusy}
                onSetErrorState={setErrorState}
                onSetImporting={(working) => {
                    const workingId = `pendingInterventionsImporting-${submission.submission.id}`;
                    if (working) {
                        SetPortalWorking(workingId);
                    } else {
                        SetPortalIdle(workingId);
                    }
                }}
                workspaceId={submission.entity.id}
                workspaceDisplayName={submission.entity.displayName || ''}
                buttonType={'small-button'}
                submissionId={submission.submission.id}
                submissionRequestId={submission.submission.submissionRequest?.id || 0}
                linkImmediate={true}
            />
        );
    }
    
    // We may or may not have this workspace metadata loaded at this point depending on what is actually displayed in the
    // workspace list. For purposes of how often this path to request access will be used, just load the ws details
    // from the server.

    const requestWorkspaceAccess = async (submission: SubmissionSearchResult): Promise<void> => {
        const workingMsg = 'SubmissionsPendingIntervention:RequestAccess';

        // To get to this point, the user will (under current circumstances) be an admin and will have permission
        // to retrieve entity details.

        SetPortalWorking(workingMsg);
        try {
            const wsDetails = await LoadWorkspaceDetails(submission.entity.id, allUsers);
            if (!wsDetails) {
                setErrorState({
                    summaryMessage: generalMessages?.unableToRetrieveWorkspaceInformationForEditing || '',
                });
                return;
            }
            if (!!wsDetails.entityDetails.workspaceUserCapabilities) {
                var userCanEdit = wsDetails.entityDetails.workspaceUserCapabilities.find(cap => cap === 'CanManageWorkspaceUsers');
                if (!!userCanEdit) {
                    SetModifyWorkspaceUsersState({
                        workspace: wsDetails.entityDetails,
                        users: wsDetails.userInformation.users || [],
                    });
                }
            }
        } finally {
            SetPortalIdle(workingMsg);
        }
    }

    const getRequestWorkspaceAccess = (submission: SubmissionSearchResult, accessible: boolean): React.ReactElement => {
        // Currently, there's no need to have a request access button.  For non-admins, the only submissions they'll get are those belonging to workspaces
        // to which they already have access.

        return (
            <Tooltip title={accessible ? '' : (craText?.addYourselfTooltip || '')}>
                <span>
                    <Button
                        type="button"
                        onClick={() => { requestWorkspaceAccess(submission) }}
                        disabled={portalBusy || accessible}
                        className={'job-icon shadowed-accounting-button'}
                        color="secondary"
                    >
                        <UsersIcon style={{ margin: 'auto' }} />
                    </Button>
                </span>
            </Tooltip>
        )
    }

    const getActionColumn = (submission: SubmissionSearchResult): React.ReactElement => {
        return (
            <div 
                className={'table-icon-column'}
                style={{
                    paddingLeft: `${!submission.entity.accessible ? '3' : '0'}px`  // adjustment for extra width of financials relink button on other lines
                }}
            >
                <Tooltip
                    key={`delete-incomplete-connection-tip-${submission.submission.id}`}
                    title={!submission.entity.accessible ? '' : (craText?.deleteDataCollection || '')}
                >
                    <span>
                        <Button
                            key={`delete-incomplete-connection-${submission.submission.id}`}
                            type="button"
                            className={'shadowed-accounting-button'}
                            onClick={() => setConfirmDelete(submission)}
                            disabled={portalBusy || !submission.entity.accessible}
                            color="secondary"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </span>
                </Tooltip>
                { getRequestWorkspaceAccess(submission, submission.entity.accessible)}
                { getLinkContent(submission) }
            </div>
        );
    }

    const tableColumns = React.useMemo(() => {
        const fullTableColumns: SortableTableColumn<SubmissionSearchResult>[] = [];

        fullTableColumns.push({
            defaultSortOrder: 'none',
            headerTitle: '',
            compare: ((ssr1: SubmissionSearchResult, ssr2: SubmissionSearchResult) => {
                return 0;
            }),
            getRowData: (rowData: SubmissionSearchResult) => {
                if (!rowData.submission) {
                    return (<></>);
                }
                return GetSubmissionStatusIconColumn(
                    brandConfig,
                    LinkMode.shareable,
                    rowData.submission.id,
                    rowData.submission.financialRecordStatus,
                    commonText,
                    rowData.submission.financialRecordId,
                    rowData.submission.financialRecordCreated

                )
            }
        });
        fullTableColumns.push({
            defaultSortOrder: 'descending',
            headerTitle: commonText?.created || '',
            compare: (s1, s2) => {
                const d1: Date | undefined = !!s1.submission?.creationTime ? new Date(s1.submission.creationTime) : undefined;
                const d2: Date | undefined = !!s2.submission?.creationTime ? new Date(s2.submission.creationTime) : undefined;

                return CompareCreationTime(d1, d2) ;
            },
            getRowData: (rowData: SubmissionSearchResult) => {
                GetSubmissionStatus(
                    rowData.submission?.financialRecordStatus,
                    commonText,
                    rowData.submission?.financialRecordId,
                    rowData.submission?.creationTime
                );

                return (
                    <span>
                        {!!rowData.submission?.creationTime ? FormatDate(new Date(rowData.submission.creationTime), formatStringMDYFullMonth) : ''}
                    </span>
                )
            }
        });
        fullTableColumns.push({
            defaultSortOrder: 'ascending',
            headerTitle: commonText?.workspaceName || '',
            compare: ((s1, s2) => CompareStringsDefaultToCreationTime(
                s1.entity.displayName,
                s2.entity.displayName,
                !!s1.submission?.creationTime ? new Date(s1.submission?.creationTime) : undefined,
                !!s2.submission?.creationTime ? new Date(s2.submission?.creationTime) : undefined
            )),
            getRowData: (rowData: SubmissionSearchResult) => {
                return (
                    <>{GetWorkspaceDisplayName(false, rowData.entity.displayName)}</>
                );
            }
        })

        fullTableColumns.push({
            defaultSortOrder: 'ascending',
            headerTitle: commonText?.requestedByEmail || '',
            compare: ((s1: SubmissionSearchResult, s2: SubmissionSearchResult) => {
                const ru1 = (s1.requestingUser?.emailAddress && s1.requestingUser.emailAddress.toLowerCase()) || '';
                const ru2 = (s2.requestingUser?.emailAddress && s2.requestingUser.emailAddress.toLowerCase()) || '';

                if (ru1 === ru2) {
                    return CompareStringsDefaultToCreationTime(
                        s1.entity.displayName,
                        s2.entity.displayName,
                        !!s1.submission?.creationTime ? new Date(s1.submission?.creationTime) : undefined,
                        !!s2.submission?.creationTime ? new Date(s2.submission?.creationTime) : undefined
                    );
                }
                return (ru1 < ru2) ? -1 : 1;
            }),
            getRowData: (rowData: SubmissionSearchResult) => {
                return (
                    <>{(rowData.requestingUser?.emailAddress && rowData.requestingUser.emailAddress) || ''}</>
                );
            }
        });

        fullTableColumns.push({
            defaultSortOrder: 'none',
            headerTitle: commonText?.actions || '',
            compare: ((ssr1: SubmissionSearchResult, ssr2: SubmissionSearchResult) => {
                return 0;
            }),
            getRowData: (rowData: SubmissionSearchResult) => {
                return getActionColumn(rowData);
            }
        })

        return fullTableColumns;

        // I do not want to include the functions in the dependency array.  That would cause the memoization to be changed
        // on just about every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissions, portalBusy])

    const deleteSubmission = (submission: SubmissionSearchResult): void => {
        DeleteSubmission(submission.submission.id)
            .then(() => {
                SubmissionDeleted(submission.entity.id, submission.submission.id);
                onSubmissionDeleted && onSubmissionDeleted(submission);
            })
            .catch(() => {
                setErrorState({
                    severity: 'Error',
                    summaryMessage: craText?.unableToDeleteCollection || '',
                });
            })
    }

    return (
        <React.Fragment>
            <Row>
                <Col className={'icon-column'} xs={5} md={3} xl={2}>
                    <FontAwesomeIcon icon={faInfoCircle} />
                </Col>
                <Col>
                    <Row>
                        <Col>
                            <h3>{craText?.title || ''}</h3>
                            <p>
                                {craText?.descriptionParagraph || ''}
                            </p>
                        </Col>
                    </Row>
                </Col>
            </Row>
            {
                !!errorState && (
                    <ErrorBanner
                        errorState={errorState}
                        onDefaultActionButton={() => {
                            setErrorState(undefined);
                        }}
                        commonText={commonText}
                    />
                )
            }
            {
                !portalBusy && !errorState && (
                    <Row>
                        <Col>
                            <SortableTable
                                brandConfig={brandConfig}
                                columns={tableColumns}
                                data={submissions}
                                sortInformation={sortState}
                                onSortParametersChanged={setSortState}
                            />
                        </Col>
                    </Row>
                )
            }
            {
                !!confirmDelete && (
                    <ConfirmModal
                        msg={(
                            <div>
                                <p>{craText?.hideConfirmationParagraph1 || ''}</p>
                                <p>{craText?.hideConfirmationParagraph2 || ''}</p>
                            </div>
                        )}
                        title={commonText?.warning || ''}
                        modalType={ConfirmModalType.yesno}
                        onTerminalButton={(yes?: boolean) => {
                            if (!!yes) {
                                deleteSubmission(confirmDelete!);
                            }
                            setConfirmDelete(undefined);
                        }}
                        commonText={commonText}
                    />
                )
            }
        </React.Fragment>
    );
}

export const SubmissionsPendingLenderIntervention = connect<InjectedReduxState, InjectedActionCreators, SubmissionsPendingLenderInterventionProps, ApplicationState>(
    (appState: ApplicationState) => {

        const result: InjectedReduxState = {
            allUsers: GetUsers(appState),
            portalBusy: GetPortalBusyState(appState),
            brandConfig: GetBrandConfig(appState),
            loggedInUser: GetLoggedInUser(appState),
            craText: GetPortalText<PortalTextCollectionsRequiringAttention>(appState, 'collectionsRequiringAttention'),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            generalMessages: GetPortalText<PortalTextGeneralMessages>(appState, 'generalMessages'),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...WorkspacesActions,
            ...UIStateActions,
            ...SubmissionHistoryActions,
        },
        dispatch
    )
)(SubmissionsPendingLenderInterventionComponent);
